import {
  FileCog,
  ScanSearch,
  Swords,
  HardDrive,
  Replace,
  ListOrdered,
  Hash,
  MapPin,
} from "lucide-react";
import { Callout, CodeBlock, Tag } from "@/components/GuidesShared";
import { StepList, BulletList } from "@/lib/guides/primitives";
import type { GuideSectionDef } from "@/lib/guides/types";

/**
 * Quick-start guides for the free tools. Each is a short "how do I use it" —
 * the tool's own /free page carries the long reference article and FAQ, so
 * nothing here repeats that prose.
 */
export const freeToolSections: GuideSectionDef[] = [
  {
    id: "server-cfg-builder",
    label: "server.cfg Builder",
    icon: <FileCog className="h-5 w-5" />,
    shortDescription: "Build a FiveM server.cfg from a form, or patch the one you already have.",
    seoTitle: "server.cfg Builder Guide — Build or Patch a FiveM Config",
    seoDescription:
      "Quick start for the free server.cfg Builder: fill in the form or paste your current config, review the checks, then copy or download the finished file.",
    keywords: [
      "FiveM server.cfg generator",
      "server.cfg builder",
      "FiveM server config",
      "sv_licenseKey setup",
      "server.cfg ensure order",
    ],
    toolHref: "/free/server-cfg",
    faqs: [
      {
        q: "Can I start from the config I already run?",
        a: "Yes. Paste it into the 'Already have a server.cfg?' box or load the .cfg file. The output then reads as your server.cfg, patched, and the builder leaves lines it has no reason to change.",
      },
      {
        q: "Do I have to drop my resources folder?",
        a: "No. It is optional input. Drop a resources/ folder or a zip if you want the builder to work from what is actually in it; otherwise fill in the form on its own.",
      },
      {
        q: "Where do my license key and passwords go?",
        a: "Nothing is uploaded. The form, license key included, is saved in this browser so a refresh does not lose it. If the machine is not yours, use the Forget saved settings button.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          The server.cfg Builder turns a form into a <Tag>server.cfg</Tag>, or patches an existing
          one, and checks the result as you go. It needs a free Workshop account (no paid plan) and
          runs in your browser.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            <>
              Optional: drop your <Tag>resources/</Tag> folder or a zip, or load one of the sample
              packs if you only want to see how it behaves.
            </>,
            <>
              Fill in the form: server name and description, port, <Tag>sv_maxclients</Tag>,
              OneSync, game build, and your keys and connection string.
            </>,
            <>
              Already running a server? Paste your current file, or use <Tag>Load a .cfg file</Tag>,
              and the builder patches it instead of writing a new one.
            </>,
            <>
              Read the checks under the preview, including what was not checked, then copy the
              file or download it.
            </>,
          ]}
        />
        <p className="text-sm font-medium text-foreground">Inputs and output</p>
        <BulletList
          items={[
            <>In: the form fields, an optional pasted or loaded <Tag>.cfg</Tag> or <Tag>.txt</Tag>, an optional resources folder or zip.</>,
            <>Out: a <Tag>server.cfg</Tag> to copy or download. A pasted config comes back patched.</>,
          ]}
        />
        <Callout type="tip">
          Paste your current config even if you only want one setting changed. You get a patched
          copy of your own file rather than a template to merge by hand.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          The full reference for every setting is on the tool page.
        </p>
      </>
    ),
  },
  {
    id: "spawn-code-finder",
    label: "Spawn Code Finder",
    icon: <ScanSearch className="h-5 w-5" />,
    shortDescription: "List every vehicle spawn code in a resources folder, with exports.",
    seoTitle: "Spawn Code Finder Guide — List Vehicle Spawn Codes in a Pack",
    seoDescription:
      "Quick start for the free Spawn Code Finder: drop a resources folder or zips, search the spawn codes per resource, and export a list, CSV or vehicles.lua.",
    keywords: [
      "FiveM spawn code list",
      "find vehicle spawn names",
      "vehicles.meta spawn code",
      "QBCore shared vehicles generator",
      "FiveM vehicle index",
    ],
    toolHref: "/free/spawn-index",
    faqs: [
      {
        q: "What does 'Guessed' mean in the totals?",
        a: "The code came from a file name rather than from a vehicles.meta entry. Treat guessed codes as leads to check, and the From vehicles.meta count as the reliable one.",
      },
      {
        q: "Which export do I want?",
        a: "Copy list (.txt) downloads a plain-text list, Spreadsheet gives CSV, and the two Lua buttons produce a vehicles.lua and a QBCore shared/vehicles.lua you can paste into a framework.",
      },
      {
        q: "A pack shows zero codes. Is that a bug?",
        a: "Every pack dropped gets its own heading, including empty ones, with the reason printed under it. Names the tool set aside are listed in the Seen and not listed section, each with an Add to the list button. Rows you add move to the Restored by hand section, where Undo puts them back.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          The Spawn Code Finder reads a pile of vehicle packs and gives you one searchable index of
          their spawn codes. It needs a free Workshop account (no paid plan) and runs in your
          browser.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            <>
              Click <Tag>Pick a folder</Tag> for a resource or your whole <Tag>resources/</Tag>{" "}
              folder, or <Tag>Pick zips</Tag> for archives. You can also drag them onto the drop
              zone. No files handy: load a sample pack.
            </>,
            <>Check the totals: resources, spawn codes, codes from vehicles.meta, guessed, duplicated across packs, and packs with no codes.</>,
            <>Use the search box to find a code or a resource, and read each pack&apos;s caveats under its heading.</>,
            <>Export what you need from the buttons above the results.</>,
          ]}
        />
        <p className="text-sm font-medium text-foreground">Inputs and output</p>
        <BulletList
          items={[
            <>In: a folder, or zip archives. Text files are opened up to 4 MB each; binary assets are only counted by name and size.</>,
            <>Out: <Tag>spawn-codes.txt</Tag>, <Tag>spawn-codes.csv</Tag>, <Tag>vehicles.lua</Tag>, and a QBCore <Tag>qb-shared-vehicles.lua</Tag>.</>,
          ]}
        />
        <Callout type="tip">
          Look at the Duplicated across packs figure first. Two packs that claim the same code is
          the finding you would otherwise only discover in game.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          The full reference is on the tool page.
        </p>
      </>
    ),
  },
  {
    id: "asset-clash-finder",
    label: "Asset Clash Finder",
    icon: <Swords className="h-5 w-5" />,
    shortDescription: "Find streamed names, spawn codes and data_files that two packs both claim.",
    seoTitle: "Asset Clash Finder Guide — Find Conflicting FiveM Resources",
    seoDescription:
      "Quick start for the free Asset Clash Finder: drop your resources, add your server.cfg for load order, and read which colliding assets are identical or different.",
    keywords: [
      "FiveM asset conflict",
      "duplicate stream names",
      "FiveM resource collision",
      "server.cfg load order",
      "FiveM clash finder",
    ],
    toolHref: "/free/clash-finder",
    faqs: [
      {
        q: "Why add my server.cfg?",
        a: "Start order decides which copy of a colliding asset the game keeps. Without the config, collisions are reported with the winner marked as unknown. Paste it or pick the file, and winners can be named.",
      },
      {
        q: "Is anything uploaded?",
        a: "Your files are not. The one outside request is the page fetching the base-game name list, which is used to recognise names that already exist in GTA.",
      },
      {
        q: "What do I do with the report?",
        a: "Download it as Markdown, CSV or JSON. The Markdown file pastes cleanly into a support ticket or a Discord thread with a pack author.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          The Asset Clash Finder scans your resources for names that more than one pack provides:
          streamed files, spawn codes and <Tag>data_file</Tag> entries. It needs a free Workshop
          account (no paid plan) and runs in your browser.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            <>
              Pick your whole <Tag>resources/</Tag> folder, or the zips you bought. Sample packs
              are there if you have nothing to hand.
            </>,
            <>
              Optional but worth doing: paste your <Tag>server.cfg</Tag> or use{" "}
              <Tag>Pick server.cfg</Tag>, so the report can say which copy wins.
            </>,
            <>
              Read the totals (resources, collisions, stream names, spawn codes, data_file), then
              go through the list under <Tag>What is being fought over</Tag>. The search box filters
              by name or resource.
            </>,
            <>
              Click <Tag>Compare the bytes</Tag> to find out whether each stream-file clash is
              identical copies or genuinely different files. Until you do, every clash reads{" "}
              <Tag>not compared</Tag>; spawn codes and data_file entries always do, because they have
              no bytes to compare. Then export the report.
            </>,
          ]}
        />
        <p className="text-sm font-medium text-foreground">Inputs and output</p>
        <BulletList
          items={[
            <>In: a folder or zips, plus an optional <Tag>server.cfg</Tag> (<Tag>.cfg</Tag> or <Tag>.txt</Tag>).</>,
            <>Out: <Tag>clash-report.md</Tag>, <Tag>clash-report.csv</Tag> or <Tag>clash-report.json</Tag>.</>,
          ]}
        />
        <Callout type="tip">
          Drop the whole resources folder rather than two suspect packs. A clash with a third
          resource you forgot about is the one that eats an afternoon.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          The full reference is on the tool page.
        </p>
      </>
    ),
  },
  {
    id: "stream-budget",
    label: "Stream Budget",
    icon: <HardDrive className="h-5 w-5" />,
    shortDescription: "See where the bytes in a resources folder go, and what duplicates waste.",
    seoTitle: "Stream Budget Guide — Measure FiveM Resource Size",
    seoDescription:
      "Quick start for the free Stream Budget tool: drop a folder or zips, see every byte grouped by type, spot wasted duplicates, and export a CSV, Markdown or JSON report.",
    keywords: [
      "FiveM stream size",
      "FiveM resource size checker",
      "duplicate files FiveM",
      "server download size",
      "FiveM streaming budget",
    ],
    toolHref: "/free/stream-budget",
    faqs: [
      {
        q: "Is the total what players download?",
        a: "No. It is disk size across every file in the drop, including server scripts and files no manifest names. The tile says so itself: disk size, not a download.",
      },
      {
        q: "Does it read inside my zips?",
        a: "The first pass reads sizes only; a zip's entry sizes come from its directory without unpacking. Duplicates are found in a picked folder, where files that share a size get a short second look. Binary files inside a zip are counted but not compared, so unpack the zip and pick the folder. That second look is on by default; untick Second pass to skip it.",
      },
      {
        q: "Can I cancel a scan on a big folder?",
        a: "Yes. A progress line with a cancel button shows while it runs. Cancelling keeps nothing.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          Stream Budget adds up everything in a resources folder, groups it by kind, and shows how
          much space duplicate files take. It needs a free Workshop account (no paid plan) and runs
          in your browser.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            <>
              Click <Tag>Pick a folder</Tag> or <Tag>Pick zips</Tag> (<Tag>.zip</Tag>), or drag a
              resource, a <Tag>resources/</Tag> folder or a zip onto the drop zone. A sample pack
              demonstrates what a duplicate costs.
            </>,
            <>Read the four headline figures: every byte in the drop, files, resources, and wasted on duplicates.</>,
            <>Use the Where the bytes went breakdown to see which kind of file dominates.</>,
            <>Export the report as CSV, Markdown or JSON.</>,
          ]}
        />
        <p className="text-sm font-medium text-foreground">Inputs and output</p>
        <BulletList
          items={[
            <>In: a folder, a single resource, or <Tag>.zip</Tag> archives. Every file is counted, scripts and metas included.</>,
            <>Out: <Tag>stream-budget.csv</Tag>, <Tag>stream-budget.md</Tag> and a JSON report.</>,
          ]}
        />
        <Callout type="tip">
          Run it on one resource at a time when you are hunting a single heavy pack, and on the whole
          folder when you want to find duplicates between packs.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          The full reference is on the tool page.
        </p>
      </>
    ),
  },
  {
    id: "spawn-code-renamer",
    label: "Spawn Code Renamer",
    icon: <Replace className="h-5 w-5" />,
    shortDescription: "Rename a vehicle's spawn code everywhere it appears, after a dry-run review.",
    seoTitle: "Spawn Code Renamer Guide — Rename a FiveM Vehicle Safely",
    seoDescription:
      "Quick start for the free Spawn Code Renamer: drop a vehicle resource, list old and new codes, review the dry-run plan and diffs, then download the renamed pack.",
    keywords: [
      "rename FiveM vehicle spawn code",
      "change vehicle model name",
      "vehicles.meta rename",
      "handlingId rename",
      "fix spawn code conflict",
    ],
    toolHref: "/free/spawn-rename",
    faqs: [
      {
        q: "Does it change my files on disk?",
        a: "No. It never touches your disk. The only output is a zip you choose to download, and the plan is shown in full before the button is available.",
      },
      {
        q: "What if a rename would collide with another code?",
        a: "The plan shows it as blocked, and nothing can be downloaded until every collision and invalid code is fixed.",
      },
      {
        q: "Can I rename several codes in one pass?",
        a: "Yes. Use Add another rename for each extra row. Each row is one old code and one new code.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          The Spawn Code Renamer changes a vehicle&apos;s spawn code across the resource that
          defines it, and shows you the exact edits before anything is built. It needs a free
          Workshop account (no paid plan) and runs in your browser.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            <>Pick the resource folder, or its zip, or drag it onto the drop zone.</>,
            <>
              In Which codes to rename, enter the old code and the new code, for example{" "}
              <Tag>20ambocr</Tag> to your new name. Add a row per extra rename.
            </>,
            <>
              Read the plan: files that would be renamed, and a diff for every file that would be
              edited. Fix anything marked blocked.
            </>,
            <>
              Click <Tag>Download renamed pack</Tag> and copy the pack files over the original,
              leaving the manifest out.
            </>,
          ]}
        />
        <p className="text-sm font-medium text-foreground">Inputs and output</p>
        <BulletList
          items={[
            <>In: a resource folder or zip. Text files are read up to 4 MB; binary assets are carried into the download unread.</>,
            <>Out: a zip of the renamed pack plus <Tag>_workshop-spawn-rename-manifest.txt</Tag>, which lists every rename and edit. Fields tied to the code, such as <Tag>modelName</Tag>, <Tag>txdName</Tag>, <Tag>handlingId</Tag> and the streamed file names, move together.</>,
          ]}
        />
        <Callout type="tip">
          Rename in a copy of the resource, then test it on a dev server before swapping it in.
          Other resources that reference the old code, such as a vehicle shop, are not part of the
          drop and still need updating.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          The full reference is on the tool page.
        </p>
      </>
    ),
  },
  {
    id: "id-bench",
    label: "ID Bench",
    icon: <ListOrdered className="h-5 w-5" />,
    shortDescription: "Find and renumber colliding mod kit, siren and light ids in carcols.meta.",
    seoTitle: "ID Bench Guide — Fix carcols.meta Mod Kit and Siren IDs",
    seoDescription:
      "Quick start for the free ID Bench: drop your resources, review colliding or out-of-range carcols ids, choose which moves to accept, and download only the changed files.",
    keywords: [
      "carcols.meta id fix",
      "FiveM mod kit id collision",
      "siren id renumber",
      "carvariations.meta kit id",
      "Siren Setting Limit Adjuster",
    ],
    toolHref: "/free/id-bench",
    faqs: [
      {
        q: "Do I have to accept every suggested move?",
        a: "No. Each move in the renumber plan has a checkbox, with Select all and Select none above them. Moves you deselect are listed in the report's Left unchecked, not applied section.",
      },
      {
        q: "What is the Siren Setting Limit Adjuster checkbox?",
        a: "Tick it if your server runs that plugin, so the siren id checks are made against how your server is set up.",
      },
      {
        q: "What does 'Refused' mean?",
        a: "Ids the tool will not move, listed with the reason. They are shown ahead of the plan so a refusal is as visible as a fix.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          ID Bench scans <Tag>carcols.meta</Tag> across your resources for mod kit, siren and light
          ids that collide or sit in the wrong range, and builds a renumber plan you approve move by
          move. It needs a free Workshop account (no paid plan) and runs in your browser.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            <>
              Pick your <Tag>resources/</Tag> folder, one resource, or zips. To try it first, load
              the 20ambocr sample.
            </>,
            <>Tick the Siren Setting Limit Adjuster box if your server uses it, and untick any resources you want left out of the scan.</>,
            <>Read the findings, then the Refused list, then the renumber plan. Deselect any move you do not want.</>,
            <>Preview the changed files, then download the zip of changed files or the report only.</>,
          ]}
        />
        <p className="text-sm font-medium text-foreground">Inputs and output</p>
        <BulletList
          items={[
            <>In: a folder or <Tag>.zip</Tag> archives containing carcols.meta files.</>,
            <>Out: <Tag>id-bench-changes.zip</Tag>, with only the files an accepted move changed, under <Tag>&lt;resource&gt;/</Tag> folders, each next to a <Tag>.bak</Tag> of the original (unless a .bak already exists), plus <Tag>id-bench-report.md</Tag> and <Tag>id-bench-manifest.json</Tag>. Or the Markdown report alone.</>,
          ]}
        />
        <CodeBlock>{`id-bench-report.md
id-bench-manifest.json
<resource>/<edited file>
<resource>/<edited file>.bak`}</CodeBlock>
        <Callout type="tip">
          If another resource also uses an id you renumber, the tool cannot see that resource unless
          you dropped it. Include everything the server loads, or re-test after applying.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          The full reference is on the tool page.
        </p>
      </>
    ),
  },
  {
    id: "joaat-hash-generator",
    label: "JOAAT Hash Generator",
    icon: <Hash className="h-5 w-5" />,
    shortDescription: "Turn a name into its JOAAT hash, as unsigned, signed or hex.",
    seoTitle: "JOAAT Hash Generator Guide — Get a GetHashKey Value",
    seoDescription:
      "Quick start for the free JOAAT Hash Generator: type a model or weapon name and copy its unsigned, signed or hex hash for use in FiveM scripts.",
    keywords: [
      "JOAAT hash generator",
      "GetHashKey online",
      "FiveM hash calculator",
      "joaat hex converter",
      "GTA V model hash",
    ],
    toolHref: "/free/joaat",
    faqs: [
      {
        q: "Does letter case matter when I type the name?",
        a: "No. The tool lowercases your text before hashing and shows the lowercased string it used, so WEAPON_PISTOL and weapon_pistol give the same result.",
      },
      {
        q: "Which of the three values should I copy?",
        a: "Match whatever the code you are pasting into expects. The tool shows unsigned, signed and hex rows, each with its own copy button.",
      },
      {
        q: "Can I hash a whole list at once?",
        a: "No. It hashes one string at a time as you type.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          The JOAAT Hash Generator gives you the hash the game would compute for a name, which is
          what <Tag>GetHashKey</Tag> returns. It needs a free Workshop account (no paid plan) and
          runs in your browser.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            <>
              Type a name in the String to hash box, such as <Tag>weapon_pistol</Tag>, or click one
              of the example names under the box.
            </>,
            <>Check the lowercased string shown above the results is the one you meant.</>,
            <>Copy the Unsigned, Signed or Hex value with the button on its row.</>,
          ]}
        />
        <p className="text-sm font-medium text-foreground">Inputs and output</p>
        <BulletList
          items={[
            <>In: one string, such as a weapon, vehicle or ped model name.</>,
            <>Out: the hash as an unsigned number, a signed number and a hex value.</>,
          ]}
        />
        <Callout type="tip">
          Paste the name exactly as it appears in the meta file or resource. A stray space or a
          typo hashes to a different, valid-looking number with no error.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          The full reference is on the tool page.
        </p>
      </>
    ),
  },
  {
    id: "coordinates-helper",
    label: "Coordinates Helper",
    icon: <MapPin className="h-5 w-5" />,
    shortDescription: "Paste GTA V coordinates in any shape and copy them as vector3, vec3 and more.",
    seoTitle: "Coordinates Helper Guide — Convert GTA V Coords to vector3",
    seoDescription:
      "Quick start for the free Coordinates Helper: paste coordinates in any format, set the decimals, and copy them as Lua vector3, vec3, vector4, a table or CSV.",
    keywords: [
      "vector3 converter",
      "FiveM coordinates formatter",
      "GTA V coords to vec3",
      "vector4 heading",
      "Lua coordinate table",
    ],
    toolHref: "/free/vector3",
    faqs: [
      {
        q: "How many numbers do I need to paste?",
        a: "At least three: x, y and z. A fourth number is read as the heading and goes into the vector4 row; without one, that row uses a heading of 0.",
      },
      {
        q: "Can I paste text from my console or a trainer?",
        a: "Yes. The input is free-form, so labelled text such as X: 215.76 Y: -810.25 Z: 30.73 works as well as vector3(...) or a comma list.",
      },
      {
        q: "How do I round the output?",
        a: "Set the Decimals field. It accepts 0 to 6.",
      },
    ],
    content: () => (
      <>
        <p className="text-sm leading-7 text-muted-foreground">
          The Coordinates Helper reads coordinates in whatever shape you copied them and rewrites
          them in the formats scripts expect. It needs a free Workshop account (no paid plan) and
          runs in your browser.
        </p>
        <p className="text-sm font-medium text-foreground">How to use it</p>
        <StepList
          steps={[
            <>Paste your coordinates into the box, or click one of the examples under it.</>,
            <>Set Decimals to the precision you want.</>,
            <>Copy the row you need with the button beside it.</>,
          ]}
        />
        <p className="text-sm font-medium text-foreground">Inputs and output</p>
        <CodeBlock>{`In:  X: 215.76  Y: -810.25  Z: 30.73
     441.1, -982.4, 30.6, 180.0`}</CodeBlock>
        <BulletList
          items={[
            <>Out: Lua vector3, Lua vec3, vector4 (with heading), C# new Vector3, Lua table, JS object, Array and Plain CSV.</>,
          ]}
        />
        <Callout type="tip">
          Paste the heading with the position when you have it. Spawn points and teleport targets
          usually want the vector4 row.
        </Callout>
        <p className="text-sm leading-7 text-muted-foreground">
          The full reference is on the tool page.
        </p>
      </>
    ),
  },
];
